<template>
  <div class="edit-single-row-modal">
    <div class="modal-header">
      <h2>{{ $t('EmailBuilder_EditSyncedRow_Title') }}</h2>
      <p class="subtitle">
        {{ $t('EditSingleRowMode_Subtitle') }}
      </p>
    </div>
    <div class="modal-content">
      <div :id="builderContainerId" class="builder"></div>
    </div>
    <div class="modal-footer">
      <el-button
        @click="handleCancel"
        :disabled="saving"
        data-qa="edit-synced-row-cancel-btn"
      >
        {{ $t('Button_Cancel') }}
      </el-button>
      <el-button
        type="primary"
        @click="handleSave"
        :loading="saving"
        data-qa="edit-synced-row-save-btn"
      >
        {{ $t('EditSingleRowMode_SaveButton') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import Bee from 'bee-plugin'
import BeeAuth from '@/common/components/email/BeeAuth'
import RestApi from '@/core/services/RestApi'
import { BeeLanguageEnum } from '@/common/enum/BeeLanguageEnum'

export default {
  name: 'EditSingleRowMode',
  props: {
    savedRow: {
      type: Object,
      required: true
    },
    onSave: Function,
    onCancel: Function
  },

  data() {
    return {
      bee: null,
      saving: false,
      updatedJSON: null,
      updatedHTML: null,
      saveResolve: null,
      initTimerId: null,
      builderDisposed: false,
      builderContainerId: 'edit-single-row-builder'
    }
  },

  created() {
    this.builderContainerId = `edit-single-row-builder-${this._uid}`
  },

  mounted() {
    this.$nextTick(() => {
      this.initTimerId = setTimeout(() => {
        if (!this.builderDisposed) {
          this.initBuilder()
        }
      }, 100)
    })
  },

  beforeDestroy() {
    this.builderDisposed = true
    this.saveResolve = null

    if (this.initTimerId) {
      clearTimeout(this.initTimerId)
    }

    const el = document.getElementById(this.builderContainerId)
    if (el && el.parentElement) {
      el.parentElement.removeChild(el)
    }
  },

  computed: {
    beeLanguage() {
      let store = this.$store || window.vue.$store
      if (!store) {
        return BeeLanguageEnum['en']
      }

      let language = BeeLanguageEnum[store.state.app.user.language]
      if (!language) {
        language = BeeLanguageEnum[store.state.app.provider.language]
      }
      return language || BeeLanguageEnum['en']
    }
  },

  methods: {
    async initBuilder() {
      try {
        const { mergeTags, customFonts } = await this.loadBuilderAssets()
        const pageJSON = this.getPageJSON()

        if (!pageJSON) {
          this.$message.error(this.$t('EditSingleRowMode_InvalidRowData'))
          this.handleCancel()
          return
        }

        const token = await BeeAuth.getToken()
        if (this.builderDisposed) {
          return
        }

        this.startBeeBuilder(
          token,
          this.createBuilderConfig({
            mergeTags,
            customFonts
          }),
          pageJSON
        )
      } catch (error) {
        console.error('Failed to initialize Edit Single Row Mode:', error)
        this.$message.error(
          this.$t('EditSingleRowMode_LoadError', {
            error: this.getErrorMessage(error)
          })
        )
        this.handleCancel()
      }
    },

    async loadBuilderAssets() {
      const projectId = window.xp.project.id
      const [attributesResponse, fontsResponse] = await Promise.all([
        RestApi.get(`projects/${projectId}/attributes`),
        RestApi.get(`projects/${projectId}/web-font`)
      ])

      return {
        mergeTags: this.buildMergeTags(attributesResponse.data || []),
        customFonts: this.buildCustomFonts(fontsResponse.data || [])
      }
    },

    getPageJSON() {
      const meta = this.parseBeeJson(this.savedRow.meta)
      const pageJSON = meta.pageJSON ? this.parseBeeJson(meta.pageJSON) : null
      return pageJSON || this.buildSingleRowPageJson(meta.rowJSON)
    },

    createBuilderConfig({ mergeTags, customFonts }) {
      return {
        container: this.builderContainerId,
        language: this.beeLanguage,
        roleHash: 'PowerUser',
        workspace: {
          editSingleRow: true
        },
        editorFonts: {
          showDefaultFonts: true,
          customFonts
        },
        mergeTags,
        onChange: json => {
          this.updatedJSON = json
        },
        onSave: (jsonFile, htmlFile) => {
          this.updatedJSON = jsonFile
          this.updatedHTML = htmlFile
          if (this.saveResolve) {
            this.saveResolve()
          }
        }
      }
    },

    startBeeBuilder(token, config, pageJSON) {
      config.uid = token.uid
      config.roleHash = token.roleHash
      this.bee = new Bee(token)
      this.bee.start(config, pageJSON)
    },

    async handleSave() {
      if (!this.bee) {
        return
      }

      try {
        await this.$confirm(
          this.$t('EditSingleRowMode_SaveConfirmMessage'),
          this.$t('EditSingleRowMode_SaveConfirmTitle'),
          {
            confirmButtonText: this.$t('EditSingleRowMode_SaveButton'),
            cancelButtonText: this.$t('Button_Cancel'),
            type: 'warning'
          }
        )
      } catch (e) {
        return
      }

      this.saving = true

      try {
        // Create a promise that resolves when onSave is called
        const savePromise = new Promise(resolve => {
          this.saveResolve = resolve
        })

        this.bee.save()

        const timeoutPromise = new Promise((resolve, reject) =>
          setTimeout(
            () => reject(new Error(this.$t('EditSingleRowMode_SaveTimeout'))),
            5000
          )
        )

        await Promise.race([savePromise, timeoutPromise])
        this.saveResolve = null

        const jsonData = this.parseBeeJson(this.updatedJSON)

        if (!jsonData) {
          this.$message.error(this.$t('EditSingleRowMode_NoChangesDetected'))
          this.saving = false
          return
        }

        const updatedRow = this.extractFirstRow(jsonData)

        if (!updatedRow) {
          this.$message.error(this.$t('EditSingleRowMode_InvalidRowData'))
          this.saving = false
          return
        }

        const response = await RestApi.patch(
          `projects/${window.xp.project.id}/content/${this.savedRow.id}/sync`,
          {
            meta: JSON.stringify({
              rowJSON: updatedRow,
              pageJSON: jsonData
            }),
            content: this.updatedHTML || null
          }
        )

        // Without this, the warning system on the next campaign validate
        // would see a synced row with no version stamp and silently skip it.
        const newVersion = response && response.data && response.data.version
        if (newVersion != null) {
          updatedRow.metadata = updatedRow.metadata || {}
          updatedRow.metadata.version = newVersion
        }

        this.saving = false
        if (this.onSave) {
          this.onSave(updatedRow)
        }
      } catch (error) {
        this.saveResolve = null
        console.error('Failed to save synced row:', error)
        this.$message.error(
          this.$t('EditSingleRowMode_SaveError', {
            error: this.getErrorMessage(error)
          })
        )
        this.saving = false
      }
    },

    buildMergeTags(attributes) {
      return attributes.map(record => {
        let path
        if (record.name.match('^[a-zA-Z]+[a-zA-Z0-9_.]{0,}$')) {
          path = record.name
        } else {
          path = "user.attributes['" + record.name.replaceAll(`'`, `\\'`) + "']"
        }

        return {
          name: record.name,
          value: `{{ ${path} }}`
        }
      })
    },

    buildCustomFonts(fonts) {
      return fonts.map(record => ({
        name: record.name,
        fontFamily: record.family,
        url: record.url
      }))
    },

    buildSingleRowPageJson(rowJson) {
      if (!rowJson) {
        return null
      }

      return {
        page: {
          rows: [this.parseBeeJson(rowJson)]
        }
      }
    },

    parseBeeJson(value) {
      return typeof value === 'string' ? JSON.parse(value) : value
    },

    extractRows(templateData) {
      if (
        templateData &&
        templateData.page &&
        Array.isArray(templateData.page.rows)
      ) {
        return templateData.page.rows
      }

      if (templateData && Array.isArray(templateData.rows)) {
        return templateData.rows
      }

      return null
    },

    extractFirstRow(templateData) {
      const rows = this.extractRows(templateData)
      return rows && rows.length > 0 ? rows[0] : null
    },

    getErrorMessage(error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return error.response.data.message
      }

      if (error && error.message) {
        return error.message
      }

      return this.$t('EditSingleRowMode_UnknownError')
    },

    handleCancel() {
      if (this.onCancel) {
        this.onCancel()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.edit-single-row-modal {
  display: flex;
  flex-direction: column;
  height: 90vh;
  width: 90vw;
  max-width: 1400px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #f5f5f5;

  h2 {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
  }

  .subtitle {
    margin: 0;
    font-size: 14px;
    color: #666;
  }
}

.modal-content {
  flex: 1;
  overflow: hidden;
  position: relative;

  .builder {
    width: 100%;
    height: 100%;
  }
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  background: #f5f5f5;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}
</style>



// WEBPACK FOOTER //
// src/common/components/email/EditSingleRowMode.vue